import React, { useEffect, useState } from 'react'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TheatricalPromotionContext'
import { BannerPlaceholder } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/BannerPlaceholder'
import FlashSaleBanner from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/DiscountBanner/FlashSaleBanner'
import { EarlyBirdBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/EarlyBirdBanner/EarlyBirdBanner'
import { GiveawayBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/GiveawayBanners/GiveawayBanner'
import { GuildGiveawayBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/GiveawayBanners/GuildGiveawayBanner'
import { NoFeesBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/NoFeesBanner/NoFeesBanner'

export interface TicketPromoBannerProps {
  className?: string
  theatricalSlug: string
  areGuildTicketsAvailable: boolean
  isGuildDataLoading?: boolean
}

export const TicketPromoBanner: React.FC<TicketPromoBannerProps> = ({
  className,
  theatricalSlug,
  areGuildTicketsAvailable,
  isGuildDataLoading = false,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { promo, isReady, refetch } = useTheatricalPromotionContext()
  const [isClient, setIsClient] = useState(false)
  const [showPlaceholder, setShowPlaceholder] = useState(true)

  useEffect(() => {
    setIsClient(true)

    const timeoutId = setTimeout(() => {
      setShowPlaceholder(false)
    }, 1500)

    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    if (isReady) {
      if (!promo?.promoType) {
        setShowPlaceholder(false)
      }
    }
  }, [isReady, promo])

  if (!isClient) {
    return null
  }

  if ((!isReady || isGuildDataLoading) && showPlaceholder) {
    if (isClient) {
      return <BannerPlaceholder />
    }
    return null
  }

  if (!promo?.promoType) return null

  const { promoType, discount, code } = promo

  const hasValidProps = (() => {
    switch (promoType) {
      case 'EARLY_BIRD':
        return Boolean(discount && code)
      case 'FLASH_SALE':
        return Boolean(discount)
      case 'NO_FEES':
        return Boolean(code)
      case 'GIVEAWAY':
        return true
      default:
        return false
    }
  })()

  if (!hasValidProps) {
    return null
  }

  if (promoType === 'EARLY_BIRD' && !isGuildDataLoading) {
    return (
      <EarlyBirdBanner
        className={''}
        theatricalSlug={theatricalSlug}
        discount={discount!}
        code={code!}
        refetch={refetch}
      />
    )
  }
  if (promoType === 'GIVEAWAY' && areGuildTicketsAvailable) {
    return <GuildGiveawayBanner theatricalSlug={theatricalSlug} className={className} />
  }
  if (promoType === 'GIVEAWAY' && !areGuildTicketsAvailable) {
    return <GiveawayBanner theatricalSlug={theatricalSlug} className={className} />
  }
  if (promoType === 'FLASH_SALE') {
    return <FlashSaleBanner className={className} theatricalSlug={theatricalSlug} discount={discount!} />
  }
  if (promoType === 'NO_FEES') {
    return <NoFeesBanner theatricalSlug={theatricalSlug} code={code!} />
  }

  return null
}
